import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { AppShellSkeleton } from './AppShellSkeleton';

interface Props {
  children: JSX.Element;
  /** Legacy: check if user has at least one of these roles (by name). Prefer `permissions`. */
  roles?: string[];
  /** Preferred: check if user has all of these permissions (AND semantics). */
  permissions?: string[];
  /** Check if user has at least one of these permissions (OR semantics). */
  anyPermissions?: string[];
}

export function ProtectedRoute({ children, roles, permissions, anyPermissions }: Props): JSX.Element {
  const { isAuthenticated, isLoading, user } = useAuth();

  // Always wait for the initial refresh to complete before rendering.
  // If we render children while isLoading is true, every page query fires
  // with no Bearer token (access token is in-memory only, lost on reload),
  // gets a 401, and triggers a second concurrent /auth/refresh via client.ts —
  // doubling the refresh calls on every single page load.
  if (isLoading) return <AppShellSkeleton />;

  if (isAuthenticated) {
    // A gate with no profile to test cannot pass. Falling through to `return
    // children` below skipped every role and permission check whenever `user`
    // was null, handing out admin pages to anyone in that state.
    if ((roles || permissions || anyPermissions) && !user) {
      return <Navigate to="/home" replace />;
    }
    if (user) {
      // When roles + anyPermissions are both provided, pass if EITHER matches (OR between groups).
      if (roles && anyPermissions) {
        const hasRole = roles.some((r) => user.roles.includes(r));
        const hasPerm = anyPermissions.some((p) => user.permissions.includes(p));
        if (!hasRole && !hasPerm) return <Navigate to="/home" replace />;
      } else {
        // Any-of permission check (OR semantics)
        if (anyPermissions && !anyPermissions.some((p) => user.permissions.includes(p))) {
          return <Navigate to="/home" replace />;
        }
        // Role-based check (legacy, used for backwards-compat)
        if (roles && !roles.some((r) => user.roles.includes(r))) {
          return <Navigate to="/home" replace />;
        }
      }
      // Permission-based check — all required (AND semantics)
      if (permissions && !permissions.every((p) => user.permissions.includes(p))) {
        return <Navigate to="/home" replace />;
      }
    }
    return children;
  }

  // Refresh done, definitely not authenticated — go to login.
  return <Navigate to="/login" replace />;
}
